import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 -- AcLink: THE ONE link grammar.
 *
 * The founder-approved visual reference (docs/design/mockups/
 * my-work-c-system-2026-09-03.html) makes a person's name, a record
 * reference and a foot door LINKS: brand ink, no underline at rest, an
 * underline on hover, a visible focus ring. Before this primitive the
 * dashboard had no styled link owner -- 47 files import react-router's Link
 * unstyled, and every "link-looking" act was a ghost button.
 *
 * Three renderings, ONE class list (.ac-link, + .ac-link--ink for a name
 * that reads as ink and turns brand on hover):
 *   - `to`   -> react-router <Link>        (in-app navigation)
 *   - `href` -> <a>                         (an external or tel/mailto door)
 *   - neither -> <button type="button">    (an act styled as a link, e.g.
 *                                           Refresh; never a navigation)
 * PRESENTATION ONLY: nothing here fetches, decides authority or writes;
 * whether a name MAY be a link is the caller's audience predicate.
 */
export type AcLinkTone = 'brand' | 'ink';

export interface AcLinkProps {
  readonly to?: string;
  readonly href?: string;
  readonly tone?: AcLinkTone;
  readonly children: ReactNode;
  readonly className?: string;
  readonly title?: string;
  readonly testId?: string;
  readonly onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'] &
    AnchorHTMLAttributes<HTMLAnchorElement>['onClick'];
  readonly 'aria-label'?: string;
  readonly target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  readonly rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
}

export function acLinkClass(tone: AcLinkTone = 'brand', className?: string): string {
  const canonical = tone === 'ink' ? 'ac-link ac-link--ink' : 'ac-link';
  return className ? `${canonical} ${className}` : canonical;
}

const AcLink = forwardRef<HTMLAnchorElement | HTMLButtonElement, AcLinkProps>(function AcLink(
  { to, href, tone = 'brand', children, className, title, testId, onClick, target, rel, ...rest },
  ref,
) {
  const cls = acLinkClass(tone, className);
  const ariaLabel = rest['aria-label'];
  if (to !== undefined) {
    return (
      <Link
        to={to}
        className={cls}
        title={title}
        data-testid={testId}
        onClick={onClick}
        aria-label={ariaLabel}
        target={target}
        rel={rel}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {children}
      </Link>
    );
  }
  if (href !== undefined) {
    return (
      <a
        href={href}
        className={cls}
        title={title}
        data-testid={testId}
        onClick={onClick}
        aria-label={ariaLabel}
        target={target}
        rel={rel}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      title={title}
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      ref={ref as React.Ref<HTMLButtonElement>}
    >
      {children}
    </button>
  );
});

export default AcLink;
